import React from "react";
import { emitLifecycle } from "@/lib/lifecycle";

/**
 * SectionErrorBoundary — small inline boundary used inside the Analyze
 * page to isolate individual analysis sections. If e.g. the
 * InstitutionalSnapshot crashes on a null sub-field, the rest of the
 * analysis (PriceChart, ScorePanel, AIInsights, KPIs) still renders.
 *
 * Phase 6.AH — granular per-section isolation. The page-level
 * AnalyzeErrorBoundary is still the outer safety net, but section-level
 * boundaries let users see MOST of the analysis even when one
 * sub-component has a bug. This dramatically improves the "MU works
 * with limited data" experience.
 *
 * Reports to backend via the same `/api/_health/frontend_error`
 * endpoint, tagged with the section name so admins can see "the
 * InstitutionalSnapshot crashed on MU" specifically.
 */
export default class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  async componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(
      `[SectionErrorBoundary:${this.props.section}] caught`,
      error, info
    );
    // Phase 6.AL — Also emit render_failed lifecycle so the Owner
    // Console can correlate the per-section crash with the analyze
    // response that triggered it.
    try {
      const lastReqId = (
        (typeof window !== "undefined" && window.__STOCKAILY_LAST_REQUEST_IDS__?.[0]?.request_id) ||
        this.props.requestId || null
      );
      emitLifecycle("render_failed", {
        ticker: this.props.ticker || null,
        request_id: lastReqId,
        extra: {
          boundary: "section",
          component: this.props.section || null,
          error_name: error?.name || "Error",
          error_message: String(error?.message || error || "").slice(0, 300),
        },
      });
    } catch { /* swallow */ }
    try {
      const backend = (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "";
      const url = `${backend}/api/_health/frontend_error`;
      const lastReqId = (
        (typeof window !== "undefined" && window.__STOCKAILY_LAST_REQUEST_IDS__?.[0]?.request_id) ||
        this.props.requestId || null
      );
      await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ticker: this.props.ticker || null,
          boundary: "section",
          component: this.props.section || null,
          error_name: error?.name || "Error",
          error_message: String(error?.message || error || "").slice(0, 500),
          error_stack: String(error?.stack || "").slice(0, 4000),
          component_stack: String(info?.componentStack || "").slice(0, 4000),
          user_agent: (typeof navigator !== "undefined") ? navigator.userAgent : null,
          url: (typeof window !== "undefined") ? window.location.href : null,
          request_id: lastReqId,
          extra: { boundary_kind: "section", section: this.props.section },
        }),
      });
    } catch (_) {
      // best effort — never let the error reporter throw
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    // Compact inline failure — keeps the page usable.
    const section = this.props.section || "section";
    return (
      <div
        className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-[12px]"
        data-testid={`section-error-${section}`}
      >
        <div className="font-semibold text-rose-700 mb-0.5">
          {this.props.label || section} couldn't load
        </div>
        <div className="text-muted-foreground">
          This panel had a rendering issue. The rest of the analysis is
          unaffected. Our team has been notified.
        </div>
        {this.props.isAdmin ? (
          <details className="mt-1.5 text-[10px]">
            <summary className="cursor-pointer text-rose-600/80">
              [admin] details
            </summary>
            <pre className="mt-1 whitespace-pre-wrap break-all bg-black/40 text-rose-200 p-2 rounded text-[10px] max-h-40 overflow-auto">
              {String(this.state.error?.message || this.state.error || "")}
            </pre>
          </details>
        ) : null}
      </div>
    );
  }
}
